import { useEffect, useState, type FormEvent } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import Header from '../components/Header'
import Footer from '../components/Footer'
import { useAuth } from '../auth/AuthProvider'
import { useLanguage } from '../i18n/LanguageProvider'
import { remainingCooldownMs, cooldownStorageKey } from '../lib/loginCooldown'

type Status = 'idle' | 'loading' | 'sent'

// Open-redirect guard (duplique le resolveNext d'AuthCallback — Phase 2 accepte
// la duplication locale plutôt qu'un util partagé, pour isolation par route).
function resolveNextParam(rawNext: string | null | undefined): string {
  if (!rawNext) return '/dashboard'
  if (!rawNext.startsWith('/')) return '/dashboard'
  if (rawNext.startsWith('//')) return '/dashboard'
  if (rawNext.includes('://')) return '/dashboard'
  return rawNext
}

// B11 (Phase 32) — anon→login email prefill source. The deterministic source
// is `?email=` in the URL (written by SlotPicker's redirect via URLSearchParams,
// which encodes special chars like `+` as `%2B`). When the URL signal is absent
// (e.g. direct visit to /login) we fall back to localStorage.lastBookingEmail
// which SlotPicker writes at email capture time. Both paths are gated by the
// canonical email regex (RESEARCH Security V5 — defense in depth even though
// GoTrue server-side validates too).
const EMAIL_FORMAT_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const LAST_BOOKING_EMAIL_KEY = 'lastBookingEmail'

function resolveInitialEmail(searchParams: URLSearchParams): string {
  const fromUrl = searchParams.get('email') ?? undefined
  if (fromUrl && EMAIL_FORMAT_REGEX.test(fromUrl)) return fromUrl
  if (typeof window === 'undefined') return ''
  const fromStorage = window.localStorage.getItem(LAST_BOOKING_EMAIL_KEY) ?? undefined
  if (fromStorage && EMAIL_FORMAT_REGEX.test(fromStorage)) return fromStorage
  return ''
}

export default function Login() {
  const { signInWithMagicLink, user, loading: authLoading } = useAuth()
  const { t } = useLanguage()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const nextParam = searchParams.get('next') ?? undefined
  const [email, setEmail] = useState(() => resolveInitialEmail(searchParams))
  const [status, setStatus] = useState<Status>('idle')
  const [error, setError] = useState<string | null>(null)
  const [cooldownRemainingMs, setCooldownRemainingMs] = useState(0)
  // Phase 114 (D-04) — coché par défaut, jamais persisté (YAGNI, UI-SPEC).
  const [marketingOptIn, setMarketingOptIn] = useState(true)

  // Redirect: si l'user est déjà loggé, évite de lui afficher le formulaire.
  // On attend que AuthProvider ait fini d'hydrater (authLoading === false) pour
  // éviter un flash-redirect inverse. Honore `?next=` (validé) pour deep-link
  // Phase 2 PackCard anon → /login?next=/packs/checkout/5.
  useEffect(() => {
    if (!authLoading && user) {
      navigate(resolveNextParam(nextParam), { replace: true })
    }
  }, [authLoading, user, navigate, nextParam])

  // Bug A (Phase 28.2): re-read the persisted cooldown on mount AND on window focus.
  // localStorage survives a reload and is shared across tabs — re-reading on focus keeps
  // the countdown honest when the link was requested in another tab. The cooldown is
  // advisory (GoTrue's 60s/email block is the authority); this just prevents the blind 429.
  useEffect(() => {
    function syncCooldown() {
      if (!email) { setCooldownRemainingMs(0); return }
      const raw = localStorage.getItem(cooldownStorageKey(email))
      const lastSentAt = raw ? Number(raw) : null
      setCooldownRemainingMs(remainingCooldownMs(lastSentAt, Date.now()))
    }
    syncCooldown()
    window.addEventListener('focus', syncCooldown)
    return () => window.removeEventListener('focus', syncCooldown)
  }, [email])

  // Tick the countdown down to 0, then stop. setInterval is the impure part —
  // remainingCooldownMs (the pure helper) does the math.
  useEffect(() => {
    if (cooldownRemainingMs <= 0) return
    const id = setInterval(() => {
      if (!email) { setCooldownRemainingMs(0); return }
      const raw = localStorage.getItem(cooldownStorageKey(email))
      const lastSentAt = raw ? Number(raw) : null
      setCooldownRemainingMs(remainingCooldownMs(lastSentAt, Date.now()))
    }, 1000)
    return () => clearInterval(id)
  }, [cooldownRemainingMs, email])

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setError(null)
    if (!EMAIL_FORMAT_REGEX.test(email)) {
      setError(t('login.error.invalidEmail'))
      return
    }
    setStatus('loading')
    try {
      await signInWithMagicLink(email, { next: nextParam, marketingOptIn })
      setStatus('sent')
      const sentAt = Date.now()
      localStorage.setItem(cooldownStorageKey(email), String(sentAt))
      setCooldownRemainingMs(remainingCooldownMs(sentAt, Date.now()))
    } catch (err) {
      setStatus('idle')
      // Supabase AuthError expose `status` (HTTP) de manière stable — on s'y fie plutôt
      // que de string-matcher `err.message`.
      const status =
        typeof err === 'object' && err !== null && 'status' in err
          ? (err as { status?: unknown }).status
          : undefined
      if (status === 429) {
        setError(t('login.error.rateLimited'))
      } else if (status === 400 || status === 422) {
        setError(t('login.error.invalidEmail'))
      } else {
        setError(t('login.error.generic'))
      }
    }
  }

  // UNE cle interpolee, pas deux fragments : l'adresse est stylee par un
  // <span> AU MILIEU de la phrase, et chaque langue place ce milieu ou elle
  // veut. On decoupe la valeur TRADUITE sur son propre marqueur — `t()` sans
  // `vars` le laisse litteral — plutot que de figer l'ordre francais dans le
  // JSX. Le harnais D-04 compare les valeurs interpolees par gabarit : une
  // concatenation maison y serait signalee comme fuite.
  const [sentBodyBefore, sentBodyAfter] = t('login.sent.body').split('{email}')

  return (
    <>
      <Header offHome />
      <main className="pt-28 pb-20 px-6 min-h-[calc(100vh-64px-80px)] flex items-center justify-center">
        <div className="max-w-[420px] w-full">
          <div className="bg-surface border border-border rounded-[8px] p-6 md:p-8">
            {status === 'sent' ? (
              <>
                <span className="text-xs font-medium uppercase tracking-[0.15em] text-text-muted mb-4 block">
                  {t('login.eyebrow')}
                </span>
                <h2 className="font-display font-[800] text-[clamp(1.4rem,3vw,2rem)] leading-tight mb-4">
                  {t('login.sent.title')}
                </h2>
                <p className="text-text-muted text-base mb-6">
                  {sentBodyBefore}<span className="text-text font-medium">{email}</span>{sentBodyAfter}
                </p>
                <button
                  type="button"
                  disabled={cooldownRemainingMs > 0}
                  onClick={() => { setStatus('idle'); setError(null); }}
                  className="text-text-muted text-sm hover:text-accent transition-colors cursor-pointer underline-offset-4 hover:underline disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {cooldownRemainingMs > 0
                    ? t('login.resendIn', { seconds: Math.ceil(cooldownRemainingMs / 1000) })
                    : t('login.resend')}
                </button>
                {/* Phase 39 D-11 — spam reminder, preempts the user-retry that
                    cascades into GoTrue 429 when the email landed in Spam.
                    Classes mirror the #login-helper paragraph below (L191). */}
                <p className="text-text-muted text-sm mt-4 text-center">
                  {t('login.sent.spam')}
                </p>
              </>
            ) : (
              <>
                <span className="text-xs font-medium uppercase tracking-[0.15em] text-text-muted mb-4 block">
                  {t('login.eyebrow')}
                </span>
                <h1 className="font-display font-[800] text-[clamp(1.8rem,4vw,2.5rem)] leading-tight tracking-[-0.02em] mb-4">
                  {t('login.title')}
                </h1>
                <p className="text-text-muted text-base mb-6">
                  {t('login.lead')}
                </p>
                <form onSubmit={handleSubmit} noValidate>
                  <label htmlFor="email" className="block text-sm font-medium text-text mb-2">
                    {t('login.emailLabel')}
                  </label>
                  <input
                    id="email"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('login.emailPlaceholder')}
                    disabled={status === 'loading'}
                    aria-invalid={!!error}
                    aria-describedby="login-helper login-error"
                    className={`bg-surface border ${error ? 'border-error' : 'border-border'} rounded-[4px] px-4 py-3 text-text placeholder:text-text-muted/50 text-base w-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-bg focus-visible:border-accent ${status === 'loading' ? 'opacity-60 cursor-not-allowed' : ''}`}
                  />
                  {error && (
                    <p id="login-error" role="alert" className="text-error text-sm mt-2">
                      {error}
                    </p>
                  )}
                  <label htmlFor="marketing-opt-in" className="mt-4 flex items-start gap-3 cursor-pointer">
                    <input
                      id="marketing-opt-in"
                      type="checkbox"
                      checked={marketingOptIn}
                      onChange={(e) => setMarketingOptIn(e.target.checked)}
                      disabled={status === 'loading'}
                      aria-describedby="marketing-opt-in-help"
                      className="h-5 w-5 mt-0.5 shrink-0 cursor-pointer accent-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-bg disabled:cursor-not-allowed disabled:opacity-60"
                    />
                    <span className="text-sm text-text">
                      {t('login.marketing.label')}
                      <span id="marketing-opt-in-help" className="block text-text-muted mt-1">
                        {t('login.marketing.help')}
                      </span>
                    </span>
                  </label>
                  <button
                    type="submit"
                    disabled={status === 'loading' || cooldownRemainingMs > 0}
                    className="mt-6 bg-accent hover:bg-accent-hover text-[#1a1209] font-medium px-6 py-3 rounded-[4px] transition-all duration-200 hover:-translate-y-px cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-bg disabled:opacity-60 disabled:cursor-not-allowed disabled:translate-y-0 w-full"
                  >
                    {cooldownRemainingMs > 0
                      ? t('login.resendIn', { seconds: Math.ceil(cooldownRemainingMs / 1000) })
                      : status === 'loading' ? t('login.sending') : t('login.submit')}
                  </button>
                  <p id="login-helper" className="text-text-muted text-sm mt-4 text-center">
                    {t('login.helper')}
                  </p>
                </form>
              </>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </>
  )
}
